
import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Check, Play, Eye, Loader, Image, Trash2, Search } from 'lucide-react';
import styles from './AddingHomepageHeroSection.module.css';
import { 
  useHeroSectionsByType, 
  useCreateHeroSection, 
  useUpdateHeroSection, 
  useDeleteHeroSection 
} from '../../../../../hooks/herosection/useherosectionHooks';
import { toast } from 'react-toastify';
import { useSearch } from '../../../../../hooks/useSearchHooks';

function AddingGarhwaliShortAudios() {
  const { heroSections, isLoading: isLoadingHeroSections, refetchHeroSections } = useHeroSectionsByType('GarhwaliShortAudios');
  const createHeroSectionMutation = useCreateHeroSection();
  const updateHeroSectionMutation = useUpdateHeroSection();
  const deleteHeroSectionMutation = useDeleteHeroSection();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const { searchResults, isLoading: isLoadingSearch } = useSearch(searchQuery, { content_type: 'audio' });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [seriesNumber, setSeriesNumber] = useState(1);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [heroSeriesToDisplay, setHeroSeriesToDisplay] = useState([]);
  const [animateHero, setAnimateHero] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // New state for image handling
  const [computerImage, setComputerImage] = useState(null);
  const [mobileImage, setMobileImage] = useState(null);
  const [computerImagePreview, setComputerImagePreview] = useState(null);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);
  
  // Refs for the file inputs
  const computerImageInputRef = useRef(null);
  const mobileImageInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);

  // Click outside search results listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Process hero sections directly from the API
  useEffect(() => {
    if (heroSections && heroSections.length > 0) {
      // Map hero sections data to display format
      const processedHeroSections = heroSections.map(hero => {
        // Use the audio_data from the response
        if (hero.audio_data) {
          return {
            ...hero,
            title: hero.audio_data.title,
            description: hero.audio_data.description,
            category_name: hero.audio_data.language, // Using language as category for now
            computer_image_url: hero.computer_image || hero.audio_data.computer_image_url,
            mobile_image_url: hero.mobile_image || hero.audio_data.mobile_image_url,
            position: hero.series_no
          };
        }
        return hero;
      });
      
      setHeroSeriesToDisplay(processedHeroSections);
    } else {
      setHeroSeriesToDisplay([]);
    }
  }, [heroSections]);
  
  useEffect(() => {
    // Trigger animation when component mounts
    setAnimateHero(true);
  }, []);

  useEffect(() => {
    if (selectedSeries) {
      // This effect is just for preview now - not adding directly to the display
      console.log("Selected series for preview:", selectedSeries);
    }
  }, [selectedSeries]);

  // Handle computer image change
  const handleComputerImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setComputerImage(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setComputerImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle mobile image change
  const handleMobileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMobileImage(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setMobileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove computer image
  const removeComputerImage = () => {
    setComputerImage(null);
    setComputerImagePreview(null);
    if (computerImageInputRef.current) {
      computerImageInputRef.current.value = '';
    }
  };

  // Remove mobile image
  const removeMobileImage = () => {
    setMobileImage(null);
    setMobileImagePreview(null);
    if (mobileImageInputRef.current) {
      mobileImageInputRef.current.value = '';
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSeriesId('');
    setSelectedSeries(null);
    setComputerImage(null);
    setMobileImage(null);
    setComputerImagePreview(null);
    setMobileImagePreview(null);
    setSearchQuery('');
  };

  const handleSeriesSelect = (series) => {
    setSelectedSeriesId(series.id);
    setSelectedSeries(series);
    setSearchQuery(series.title);
    setShowSearchResults(false);
  };

  const handleSearchFocus = () => {
    if (searchQuery.trim().length > 0) {
      setShowSearchResults(true);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSearchResults(true);
    // Clear selection if search changes
    if (selectedSeriesId && e.target.value !== selectedSeries?.title) {
      setSelectedSeriesId('');
      setSelectedSeries(null);
    }
  };

  const handleSave = async () => {
    if (!selectedSeries || !selectedSeriesId) return;
    
    setIsSubmitting(true);
    try {
      // Create FormData object to send files
      const formData = new FormData();
      formData.append('series_no', seriesNumber.toString());
      formData.append('audios_id', selectedSeriesId);
      formData.append('type', 'GarhwaliShortAudios');
      formData.append('is_active', 'true');
      
      // Append images if they exist
      if (computerImage) {
        formData.append('computer_image', computerImage);
      }
      
      if (mobileImage) {
        formData.append('mobile_image', mobileImage);
      }
      
      // Check if there's an existing hero section at this position
      const existingHero = heroSections.find(hero => hero.series_no === seriesNumber);
      
      if (existingHero) {
        // Update existing hero section
        await updateHeroSectionMutation.mutateAsync({
          id: existingHero.id,
          formData
        });
      } else {
        // Create new hero section
        await createHeroSectionMutation.mutateAsync(formData);
      }
      
      // Refresh hero sections
      await refetchHeroSections();
      
      toast.success('Hero section saved successfully!');
      setIsModalOpen(false);
      setSelectedSeriesId('');
      setSelectedSeries(null);
      setComputerImage(null);
      setMobileImage(null);
      setComputerImagePreview(null);
      setMobileImagePreview(null);
      setSearchQuery('');
    } catch (error) {
      console.error('Error saving hero section:', error);
      toast.error('Failed to save hero section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeHeroItem = async (id) => {
    try {
      await deleteHeroSectionMutation.mutateAsync(id);
      // Refresh hero sections after deletion
      await refetchHeroSections();
      toast.success('Hero section removed successfully!');
    } catch (error) {
      console.error('Error removing hero section:', error);
      toast.error('Failed to remove hero section');
    }
  };

  if (isLoadingHeroSections) {
    return (
      <div className={styles.loadingContainer}>
        <Loader className={styles.loadingSpinner} />
        <p>Loading hero sections...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.header} ${animateHero ? styles.fadeIn : ''}`}>
        <h1>Garhwali Short Stories Hero Section</h1>
        <button 
          className={styles.addButton}
          onClick={handleOpenModal}
          disabled={heroSeriesToDisplay.length >= 4}
        >
          <Plus size={20} />
          Add Hero
        </button>
      </div>

      {/* Display existing hero settings */}
      <div className={styles.currentHero}>
        {heroSeriesToDisplay.length > 0 ? (
          <div className={`${styles.heroGrid} ${animateHero ? styles.slideInUp : ''}`}>
            {heroSeriesToDisplay.map((item) => (
              <div key={item?.id} className={styles.heroCard}>
                <div className={styles.heroImageContainer}>
                  {/* Use custom hero image if available, otherwise fallback to series image */}
                  <img 
                    src={item?.computer_image || item?.computer_image_url} 
                    alt={item?.title} 
                  />
                  <div className={styles.heroOverlay}>
                    <div className={styles.heroActions}>
                      <button className={styles.playButton}>
                        <Play size={24} />
                      </button>
                      <button 
                        className={styles.removeButton} 
                        onClick={() => removeHeroItem(item?.id)}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className={styles.positionBadge}>Position {item?.position}</div>
                  </div>
                </div>
                <div className={styles.heroContent}>
                  <h3>{item?.title}</h3>
                  {item?.category_name && (
                    <div className={styles.heroMeta}>
                      <span className={styles.category}>{item?.category_name}</span>
                    </div>
                  )}
                  <p className={styles.description}>{item?.description}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <h2>No Hero Sections Added Yet</h2>
            <p>Click the "Add Hero" button to showcase featured content on your Garhwali short stories.</p>
            <button className={styles.addEmptyButton} onClick={handleOpenModal}>
              <Plus size={20} />
              Add Your First Hero Section
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${styles.fadeInScale}`}>
            <div className={styles.modalHeader}>
              <h2>Add Hero Section</h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="seriesNumber">Hero Position (1-4):</label> 
                <div className={styles.positionSelector}>
                  {[1, 2, 3, 4].map(num => (
                    <button
                      key={num}
                      className={`${styles.positionButton} ${seriesNumber === num ? styles.activePosition : ''}`}
                      onClick={() => setSeriesNumber(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="seriesSelect">Search Audio Content:</label>
                <div className={styles.searchContainer}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for audio content..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    className={styles.searchInput}
                  />
                  <div className={styles.searchIcon}>
                    <Search size={18} />
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showSearchResults && searchQuery.trim() !== '' && (
                    <div 
                      ref={searchDropdownRef}
                      className={styles.searchResultsDropdown}
                    >
                      {isLoadingSearch ? (
                        <div className={styles.loadingResults}>
                          <Loader size={20} className={styles.spinner} />
                          <span>Searching...</span>
                        </div>
                      ) : searchResults && searchResults.length > 0 ? (
                        searchResults
                          .filter(item => item.content_type === 'audio')
                          .map(item => (
                            <div 
                              key={item.id} 
                              className={styles.searchResultItem}
                              onClick={() => handleSeriesSelect(item)}
                            >
                              <div className={styles.searchResultImage}>
                                <img 
                                  src={item.computer_image_url} 
                                  alt={item.title} 
                                />
                              </div>
                              <div className={styles.searchResultInfo}>
                                <div className={styles.searchResultTitle}>{item.title}</div>
                                <div className={styles.searchResultCategory}>{item.category_name}</div>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className={styles.noResults}>
                          No audio content found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* New image upload section */}
              <div className={styles.imageUploadSection}>
                <div className={styles.formGroup}>
                  <label>Desktop Hero Image (Optional):</label>
                  <div className={styles.imageUploadContainer}>
                    {computerImagePreview ? (
                      <div className={styles.imagePreviewWrapper}>
                        <img 
                          src={computerImagePreview} 
                          alt="Desktop preview" 
                          className={styles.imagePreview} 
                        />
                        <button 
                          type="button" 
                          className={styles.removeImageButton}
                          onClick={removeComputerImage}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.imageUploadButton}>
                        <input
                          type="file"
                          ref={computerImageInputRef}
                          accept="image/*"
                          onChange={handleComputerImageChange}
                          className={styles.fileInput}
                          id="computerImageInput"
                        />
                        <label htmlFor="computerImageInput" className={styles.uploadLabel}>
                          <Image size={24} />
                          <span>Upload Desktop Image</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <small className={styles.imageHint}>Recommended size: 1920x1080px</small>
                </div>
                
                <div className={styles.formGroup}>
                  <label>Mobile Hero Image (Optional):</label>
                  <div className={styles.imageUploadContainer}>
                    {mobileImagePreview ? (
                      <div className={styles.imagePreviewWrapper}>
                        <img 
                          src={mobileImagePreview} 
                          alt="Mobile preview" 
                          className={styles.imagePreview} 
                        />
                        <button 
                          type="button" 
                          className={styles.removeImageButton}
                          onClick={removeMobileImage}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.imageUploadButton}>
                        <input
                          type="file"
                          ref={mobileImageInputRef}
                          accept="image/*"
                          onChange={handleMobileImageChange}
                          className={styles.fileInput}
                          id="mobileImageInput"
                        />
                        <label htmlFor="mobileImageInput" className={styles.uploadLabel}>
                          <Image size={24} />
                          <span>Upload Mobile Image</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <small className={styles.imageHint}>Recommended size: 375x667px</small>
                </div>
              </div>
              
              {selectedSeriesId && selectedSeries && (
                <div className={`${styles.previewSection} ${styles.slideIn}`}>
                  <h3>Preview</h3>
                  <div className={styles.previewCard}>
                    <div className={styles.previewImage}>
                      {/* Show uploaded image preview if available, otherwise show series image */}
                      {computerImagePreview ? (
                        <img
                          src={computerImagePreview}
                          alt="Custom hero"
                          className={styles.previewHeroImage}
                        />
                      ) : selectedSeries && (
                        <img
                          src={selectedSeries.computer_image_url}
                          alt={selectedSeries.title}
                        />
                      )}
                    </div>
                    {selectedSeries && (
                      <div className={styles.previewInfo}>
                        <h4>{selectedSeries.title}</h4>
                        <div className={styles.previewMeta}>
                          <span className={styles.category}>{selectedSeries.category_name}</span>
                          <span className={styles.previewStats}>
                            <Eye size={14} />
                            <span>{selectedSeries.views_count?.toLocaleString() || 0}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelButton} 
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSave}
                disabled={!selectedSeriesId || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader size={16} className={styles.spinner} />
                ) : (
                  <Check size={16} />
                )}
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



export default AddingGarhwaliShortAudios;